'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { addToCart } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { canAddDirectly } from '@eliadmin/core/src/components/nt/account/favorites/favorites-data';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';

/** ISO code of the shop currency for analytics – the lookup of the core cart button. */
function useCurrencyIso() {
  const locale = useLocaleStore(state => state.currentLocale);
  const symbol = useGlobalStore(state => state.generalLayout.currency);
  const { data } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
  });
  return data?.data.find(currency => currency.symbol === symbol)?.id || symbol;
}

/**
 * „Pridať všetko do košíka“: every favourite that needs no choice (`canAddDirectly`) goes to the
 * cart with the request, minimum quantity and analytics event of the core cart button, one after
 * another (one cart). The cart is refreshed once after the batch, then the „Pridané do košíka“
 * drawer opens like after a single product. A favourite with variants stays out – adding a guessed variant
 * could put the wrong toner in the cart. A failed batch shows the core error toast.
 */
export function useAddAllToCart() {
  const { t } = useTranslations();
  const { toast } = useToast();
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(state => state.me?.authToken);
  const openDialog = useDialogStore(state => state.open);
  const queryClient = useQueryClient();
  const currency = useCurrencyIso();

  return useMutation({
    mutationFn: async (products: Product[]) => {
      const added: Product[] = [];
      try {
        for (const product of products.filter(canAddDirectly)) {
          try {
            await addToCart({
              locale,
              authToken,
              body: {
                item: { product: { id: product.id }, sub_items: { services: [], packages: [] } },
                qty: normalizeProductQuantity(1, product),
              },
            });
          } catch {
            continue;
          }
          added.push(product);
          trackEcommerce.addToCart({
            id: String(product.id),
            name: product.name,
            price: getProductPriceDisplay(product).currentWithVat ?? 0,
            currency,
          });
        }
      } finally {
        // Before the drawer opens, so it never shows the cart of before the batch.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [CacheKey.CART] }),
          queryClient.invalidateQueries({ queryKey: [CacheKey.SHIPMENTS] }),
          queryClient.invalidateQueries({ queryKey: [CacheKey.CART_PAYMENTS] }),
        ]);
      }
      if (!added.length) throw new Error('No favourite was added to the cart');
      return added;
    },
    onSuccess: added =>
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={added[0]} title="text631" />,
      }),
    onError: () =>
      toast({ title: t('text1472'), description: t('text1320'), variant: 'destructive' }),
  });
}
